import React, { useState, useEffect } from 'react';
import { ChevronLeft } from 'lucide-react';
import Swal from 'sweetalert2';
import { createReview } from '../services/reviewServices';
import { useAuth } from '../../USER/Auth/AuthContext';
import StarRatingPicker from './components/StarRatingPicker';
import ReviewVehicleIllustration from './components/ReviewVehicleIllustration';
import ReviewDriverHeader from './components/ReviewDriverHeader';
import { BryButton } from '../../UI/BryUI';

const ReviewModal = ({ show, onHide, service, reviewType }) => {
  const { currentUser, isDriver } = useAuth();
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [saveAsFavorite, setSaveAsFavorite] = useState(false);
  const [isDriverFavorite, setIsDriverFavorite] = useState(false);

  const isCustomerReview = reviewType === 'customer_to_driver' || (!isDriver && reviewType !== 'driver_to_customer');
  const reviewedUserId = isCustomerReview ? service?.driver_uid : service?.user_uid;
  const reviewedUserName = service?.[isCustomerReview ? 'driver_name' : 'customer_name'] || (isCustomerReview ? 'Conductor Bryemma' : 'Pasajero Bryemma');

  useEffect(() => {
    if (show && isCustomerReview && currentUser && reviewedUserId) {
      import('../../../services/roleViewsServices').then(({ isFavorite }) => {
        isFavorite(currentUser.id, reviewedUserId).then(result => {
          setIsDriverFavorite(result);
          setSaveAsFavorite(result);
        }).catch(err => console.error('Error checking favorite:', err));
      });
    }
  }, [show, isCustomerReview, currentUser, reviewedUserId]);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (rating === 0) {
      Swal.fire({
        title: 'Atención',
        text: 'Por favor selecciona una calificación de 1 a 5 estrellas',
        icon: 'warning',
        confirmButtonColor: 'var(--bry-accent, #0EABB0)'
      });
      return;
    }

    setSubmitting(true);
    try {
      await createReview({
        service_id: service?.service_id || service?.id,
        reviewer_id: currentUser?.id,
        reviewed_id: reviewedUserId,
        rating,
        comment: comment.trim(),
        review_type: reviewType || 'customer_to_driver'
      });

      if (isCustomerReview && saveAsFavorite && !isDriverFavorite && currentUser && reviewedUserId) {
        try {
          const { addFavorite } = await import('../../../services/roleViewsServices');
          await addFavorite(currentUser.id, reviewedUserId);
        } catch (favErr) {
          console.error('Error saving favorite:', favErr);
        }
      }

      Swal.fire({
        icon: 'success',
        title: '¡Evaluación Enviada!',
        text: 'Muchas gracias por valorar la experiencia de tu viaje.',
        confirmButtonColor: 'var(--bry-accent, #0EABB0)'
      });

      setRating(0);
      setComment('');
      onHide();
    } catch (err) {
      console.error('Error enviando reseña:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.message || 'No se pudo enviar la evaluación.',
        confirmButtonColor: 'var(--bry-accent, #0EABB0)'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!show) return null;

  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center p-3"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)', backdropFilter: 'blur(8px)', zIndex: 10000 }}
    >
      <div
        className="rounded-4 p-4 shadow-lg w-100 text-dark"
        style={{ maxWidth: '440px', backgroundColor: '#FFFFFF', border: '1px solid var(--bry-accent, #0EABB0)' }}
      >
        <div className="d-flex align-items-center justify-content-between mb-3">
          <button className="btn btn-link text-secondary p-0" onClick={onHide}>
            <ChevronLeft size={24} />
          </button>
          <h6 className="fw-bold mb-0">Calificar Servicio</h6>
          <div style={{ width: 24 }} />
        </div>

        <ReviewVehicleIllustration serviceType={service?.service_type} />
        <ReviewDriverHeader
          name={reviewedUserName}
          avatarUrl={service?.driver_avatar_url || service?.avatar_url}
          vehicleModel={service?.vehicle_model}
          isCustomerReview={isCustomerReview}
        />

        <StarRatingPicker rating={rating} setRating={setRating} hover={hover} setHover={setHover} />

        <div className="mb-3">
          <textarea
            rows={3}
            className="form-control text-white border-secondary border-opacity-50 rounded-3 p-3"
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', fontSize: '0.9rem' }}
            placeholder="Escribe un comentario opcional sobre tu experiencia..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </div>

        <BryButton
          variant="primary"
          className="w-100 py-2 fw-bold"
          isLoading={submitting}
          onClick={handleSubmit}
          style={{ backgroundColor: 'var(--bry-accent, #0EABB0)', color: '#ffffff', border: 'none' }}
        >
          Enviar Calificación
        </BryButton>
      </div>
    </div>
  );
};

export default ReviewModal;
